import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { giftCardsApi } from '../api/giftCards'
import EstadoBadgeGiftCard, { LABELS_GIFTCARD } from '../components/EstadoBadgeGiftCard'
import Boton from '../components/Boton'
import Modal from '../components/Modal'
import ModalListaClientes from '../components/ModalListaClientes'
import ModalMandarGiftCard from '../components/ModalMandarGiftCard'
import { fmtFecha, fmtFechaHora, fmtMonto, toISOLocal } from '../utils/fecha'
import { descargarArchivo } from '../utils/descargar'
import { colors, text, sx } from '../design/tokens'

const cardSx = { ...sx.card, marginBottom: 14 }
const lblSx = {
  fontSize: text.meta, fontWeight: 600, color: colors.textSec,
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2,
}
const inputSx = {
  width: '100%', padding: '8px 10px', border: `1px solid ${colors.textPlh}`,
  borderRadius: 6, fontSize: text.body, boxSizing: 'border-box',
}

const LABELS_CANJE = { reservado: 'Reservado', aplicado: 'Aplicado', liberado: 'Liberado' }

export default function GiftCardDetallePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [card, setCard] = useState(null)
  const [modal, setModal] = useState(null) // 'anular' | 'extender' | 'editar' | 'facturar' | 'mandar'
  const [motivo, setMotivo] = useState('')
  const [venceEl, setVenceEl] = useState('')
  const [edit, setEdit] = useState({})
  const [clienteNV, setClienteNV] = useState(null) // elegido en el modal facturar
  const [buscarCliente, setBuscarCliente] = useState(false)
  const [procesando, setProcesando] = useState(false)
  const [error, setError] = useState(null)
  const [avisoMail, setAvisoMail] = useState('')

  const cargar = useCallback(async () => {
    const { data } = await giftCardsApi.obtener(id)
    setCard(data)
  }, [id])

  useEffect(() => { cargar() }, [cargar])

  const accion = async (fn) => {
    setError(null)
    setProcesando(true)
    try {
      await fn()
      setModal(null)
      await cargar()
    } catch (e) {
      const errs = e.response?.data?.errors
      setError(errs ? Object.values(errs).flat().join(' ') : (e.response?.data?.message || 'No se pudo completar.'))
    } finally {
      setProcesando(false)
    }
  }

  if (!card) return <div style={{ color: colors.textMeta, padding: 24 }}>Cargando…</div>

  return (
    <div style={{ maxWidth: 860 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Boton variante="ghost" sm onClick={() => navigate(-1)}>← Volver</Boton>
        <h1 style={{ margin: 0, ...sx.mono, fontSize: text.display, userSelect: 'all' }}>{card.codigo}</h1>
        <EstadoBadgeGiftCard estado={card.estado} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!card.nota_venta_id && card.estado !== 'anulada' && (
            <Boton variante="primary" sm onClick={() => { setClienteNV(card.cliente || null); setError(null); setModal('facturar') }}>
              🧾 Crear nota de venta
            </Boton>
          )}
          <Boton variante="secondary" sm onClick={() => descargarArchivo(`/gift-cards/${card.id}/pdf`, `gift_card_${card.codigo}.pdf`)}>
            ⬇ PDF
          </Boton>
          <Boton variante="secondary" sm
            onClick={() => descargarArchivo(`/gift-cards/${card.id}/arte`, `Gift-Card-${card.codigo}.jpg`).catch(() => window.alert('El arte se genera en el servidor — no disponible en este entorno.'))}
            title="La tarjeta como imagen (la misma que viaja en el mail)">
            🎨 Tarjeta
          </Boton>
          <Boton variante="secondary" sm onClick={() => { setEdit({
            beneficiario_nombre: card.beneficiario_nombre || '',
            beneficiario_email: card.beneficiario_email || '',
            dedicatoria: card.dedicatoria || '',
          }); setError(null); setModal('editar') }}>
            ✏️ Editar
          </Boton>
          {['activa', 'pendiente'].includes(card.estado) && (
            <Boton variante="secondary" sm title="Cargar o corregir el mail del destinatario y mandarla (o reenviarla si ya salió)"
              onClick={() => { setAvisoMail(''); setModal('mandar') }}>
              {card.mail_enviado_at ? '✉️ Reenviar' : '✉️ Mandar'}
            </Boton>
          )}
          {['activa', 'vencida'].includes(card.estado) && (
            <Boton variante="secondary" sm onClick={() => { setVenceEl(card.vence_el?.slice(0, 10) || toISOLocal(new Date())); setError(null); setModal('extender') }}>
              📅 Extender
            </Boton>
          )}
          {card.estado !== 'anulada' && (
            <Boton variante="destructive" sm onClick={() => { setMotivo(''); setError(null); setModal('anular') }}>
              Anular
            </Boton>
          )}
        </div>
      </div>

      {avisoMail && <div style={{ ...cardSx, color: colors.ok, fontSize: text.sec }}>✓ {avisoMail}</div>}
      {modal === 'mandar' && (
        <ModalMandarGiftCard card={card} onClose={() => setModal(null)}
          onEnviada={async (msg) => { setAvisoMail(msg); await cargar() }} />
      )}

      {/* Resumen */}
      <div style={cardSx}>
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          <div>
            <div style={lblSx}>Valor</div>
            <div style={{ fontSize: 20, fontWeight: 700, ...sx.mono }}>$ {fmtMonto(card.monto_inicial)}</div>
          </div>
          <div>
            <div style={lblSx}>Saldo</div>
            <div style={{ fontSize: 20, fontWeight: 700, ...sx.mono }}>$ {fmtMonto(card.saldo)}</div>
          </div>
          <div>
            <div style={lblSx}>Disponible (menos reservas)</div>
            <div style={{ fontSize: 20, fontWeight: 700, ...sx.mono, color: colors.link }}>$ {fmtMonto(card.saldo_disponible)}</div>
          </div>
          <div>
            <div style={lblSx}>Vence</div>
            <div style={{ fontSize: text.title, ...sx.mono }}>{card.vence_el ? fmtFecha(card.vence_el) : '—'}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginTop: 16, fontSize: text.body }}>
          <div>
            <div style={lblSx}>Cliente</div>
            <div>{card.cliente?.nombre || '—'}</div>
          </div>
          <div>
            <div style={lblSx}>Beneficiario</div>
            <div>{card.beneficiario_nombre || '—'}{card.beneficiario_email && <span style={{ color: colors.textSec }}> · {card.beneficiario_email}</span>}</div>
            <div style={{ fontSize: text.sec, color: card.mail_enviado_at ? colors.ok : colors.textMeta }}>
              {card.mail_enviado_at ? `✉ mandada ${fmtFechaHora(card.mail_enviado_at)}` : '✉ sin mandar'}
            </div>
          </div>
          {card.nota_venta && (
            <div>
              <div style={lblSx}>Vendida en</div>
              <a
                style={{ color: colors.link, cursor: 'pointer', textDecoration: 'none' }}
                onClick={() => navigate(`/notas-venta/${card.nota_venta.id}`)}
              >
                {card.nota_venta.numero_lgr}
              </a>
            </div>
          )}
          {card.motivo && (
            <div>
              <div style={lblSx}>Motivo</div>
              <div>{card.motivo}</div>
            </div>
          )}
          <div>
            <div style={lblSx}>Emitida por</div>
            <div>{card.emisor?.nombre || '—'} · <span style={sx.mono}>{fmtFecha(card.created_at)}</span></div>
          </div>
        </div>

        {card.dedicatoria && (
          <div style={{ marginTop: 14, padding: '10px 14px', borderLeft: `3px solid ${colors.borderStrong}`, background: colors.bgSoft, fontStyle: 'italic', fontSize: text.body }}>
            {card.dedicatoria}
          </div>
        )}

        {card.estado === 'anulada' && (
          <div style={{ marginTop: 14, background: colors.errBg, color: colors.err, padding: '8px 12px', borderRadius: 6, fontSize: text.sec }}>
            Anulada el {fmtFechaHora(card.anulada_at)} — {card.motivo_anulacion}
          </div>
        )}
        {card.observada_at && (
          <div style={{ marginTop: 14, background: colors.warnBg, color: colors.warn, padding: '8px 12px', borderRadius: 6, fontSize: text.sec }}>
            👁 Observada desde el {fmtFechaHora(card.observada_at)} — {card.observada_motivo}
          </div>
        )}
      </div>

      {/* Canjes */}
      {(card.canjes || []).length > 0 && (
        <div style={cardSx}>
          <h3 style={{ marginTop: 0, fontSize: text.title }}>Canjes</h3>
          {card.canjes.map(c => (
            <div key={c.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '6px 0', fontSize: text.body, borderBottom: `1px solid ${colors.borderSoft}` }}>
              <span style={{ ...sx.mono, fontWeight: 600 }}>$ {fmtMonto(c.monto)}</span>
              <span style={{ color: colors.textSec }}>{LABELS_CANJE[c.estado] || c.estado}</span>
              {c.nota_venta && (
                <a
                  style={{ color: colors.link, cursor: 'pointer' }}
                  onClick={() => navigate(`/notas-venta/${c.nota_venta.id}`)}
                >
                  {c.nota_venta.numero_lgr}
                </a>
              )}
              <span style={{ marginLeft: 'auto', ...sx.mono, color: colors.textMeta, fontSize: text.meta }}>{fmtFechaHora(c.created_at)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Movimientos (ledger) */}
      <div style={cardSx}>
        <h3 style={{ marginTop: 0, fontSize: text.title }}>Movimientos</h3>
        {(card.movimientos || []).map(m => (
          <div key={m.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '6px 0', fontSize: text.body, borderBottom: `1px solid ${colors.borderSoft}` }}>
            <span style={{ ...sx.mono, fontWeight: 600, color: m.monto < 0 ? colors.err : colors.ok, minWidth: 110 }}>
              {m.monto < 0 ? '−' : '+'} $ {fmtMonto(Math.abs(m.monto))}
            </span>
            <span>{m.tipo === 'emision' ? 'Emisión' : m.tipo === 'reversa_canje' ? 'Devolución (venta anulada)' : 'Canje'}</span>
            <span style={{ color: colors.textSec }}>{m.detalle}</span>
            <span style={{ marginLeft: 'auto', ...sx.mono, color: colors.textSec }}>saldo $ {fmtMonto(m.saldo_resultante)}</span>
            <span style={{ ...sx.mono, color: colors.textMeta, fontSize: text.meta }}>{fmtFechaHora(m.created_at)}</span>
          </div>
        ))}
      </div>

      {/* Historial de estados */}
      {(card.historial || []).length > 0 && (
        <div style={cardSx}>
          <h3 style={{ marginTop: 0, fontSize: text.title }}>Historial</h3>
          {card.historial.map(h => (
            <div key={h.id} style={{ display: 'flex', gap: 10, fontSize: text.sec, color: colors.textSec, padding: '3px 0' }}>
              <span style={sx.mono}>{fmtFechaHora(h.created_at)}</span>
              <span>{h.estado_anterior ? `${LABELS_GIFTCARD[h.estado_anterior] || h.estado_anterior} → ` : ''}{LABELS_GIFTCARD[h.estado_nuevo] || h.estado_nuevo}</span>
              {h.usuario && <span>· {h.usuario.nombre}</span>}
              {h.motivo && <span>· {h.motivo}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Consultas: quién validó el código y desde dónde (4 oct 2026) */}
      <div style={cardSx}>
        <h3 style={{ marginTop: 0, fontSize: text.title }}>Consultas</h3>
        {(card.consultas || []).length === 0
          ? <p style={{ fontSize: text.sec, color: colors.textSec, margin: 0 }}>Nadie consultó esta tarjeta desde que se registran las consultas (4 oct 2026).</p>
          : card.consultas.map((q, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, fontSize: text.sec, color: colors.textSec, padding: '3px 0', flexWrap: 'wrap' }}>
              <span style={sx.mono}>{fmtFechaHora(q.created_at)}</span>
              <span style={{ color: colors.text, fontWeight: 600 }}>{q.usuario || (q.detalle?.startsWith('Web') ? 'Cliente en la web' : '—')}</span>
              {q.ip && <span style={sx.mono}>IP {q.ip}</span>}
              <span>· {q.detalle}</span>
            </div>
          ))}
      </div>

      {/* Modales */}
      {modal === 'facturar' && (
        <Modal title="Crear nota de venta" onClose={() => setModal(null)}>
          <p style={{ fontSize: text.body, color: colors.textSec, marginTop: 0 }}>
            Crea la NV en borrador con la línea de la gift card por{' '}
            <b style={sx.mono}>$ {fmtMonto(card.monto_inicial)}</b> para facturarla por Presea.
            La tarjeta queda vinculada a esa NV (no se vuelve a emitir al facturar).
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{ flex: 1 }}>
              <div style={lblSx}>Cliente a facturar *</div>
              <div style={{ fontSize: text.body }}>{clienteNV?.nombre || <span style={{ color: colors.textMeta }}>Sin elegir</span>}</div>
            </div>
            <Boton variante="secondary" sm onClick={() => setBuscarCliente(true)}>
              🔍 {clienteNV ? 'Cambiar' : 'Elegir cliente'}
            </Boton>
          </div>
          {error && <div style={{ color: colors.err, fontSize: text.sec, marginTop: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <Boton variante="ghost" onClick={() => setModal(null)}>Cancelar</Boton>
            <Boton
              variante="primary"
              disabled={procesando || !clienteNV}
              onClick={async () => {
                setError(null)
                setProcesando(true)
                try {
                  const { data } = await giftCardsApi.crearNV(card.id, { cliente_id: clienteNV.id })
                  navigate(`/notas-venta/${data.id}`)
                } catch (e) {
                  const errs = e.response?.data?.errors
                  setError(errs ? Object.values(errs).flat().join(' ') : (e.response?.data?.message || 'No se pudo crear la NV.'))
                  setProcesando(false)
                }
              }}
            >
              {procesando ? 'Creando…' : 'Crear NV'}
            </Boton>
          </div>
          {buscarCliente && (
            <ModalListaClientes
              onSelect={c => { setClienteNV(c); setBuscarCliente(false) }}
              onClose={() => setBuscarCliente(false)}
            />
          )}
        </Modal>
      )}

      {modal === 'anular' && (
        <Modal title="Anular gift card" onClose={() => setModal(null)}>
          <p style={{ fontSize: text.body, color: colors.textSec, marginTop: 0 }}>
            Solo se puede anular una gift card sin canjes. La anulación es definitiva.
          </p>
          <label style={lblSx}>Motivo *</label>
          <input value={motivo} onChange={e => setMotivo(e.target.value)} style={inputSx} placeholder="Ej: emitida por error" />
          {error && <div style={{ color: colors.err, fontSize: text.sec, marginTop: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <Boton variante="ghost" onClick={() => setModal(null)}>Cancelar</Boton>
            <Boton variante="destructive" disabled={procesando || !motivo.trim()} onClick={() => accion(() => giftCardsApi.anular(card.id, motivo))}>
              {procesando ? 'Anulando…' : 'Anular'}
            </Boton>
          </div>
        </Modal>
      )}

      {modal === 'extender' && (
        <Modal title="Extender vencimiento" onClose={() => setModal(null)}>
          <p style={{ fontSize: text.body, color: colors.textSec, marginTop: 0 }}>
            {card.estado === 'vencida' ? 'La gift card vuelve a estar activa con la nueva fecha.' : 'Nueva fecha de vencimiento.'}
          </p>
          <label style={lblSx}>Vence el *</label>
          <input type="date" value={venceEl} onChange={e => setVenceEl(e.target.value)} style={inputSx} />
          {error && <div style={{ color: colors.err, fontSize: text.sec, marginTop: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <Boton variante="ghost" onClick={() => setModal(null)}>Cancelar</Boton>
            <Boton variante="primary" disabled={procesando || !venceEl} onClick={() => accion(() => giftCardsApi.extender(card.id, venceEl))}>
              {procesando ? 'Guardando…' : 'Extender'}
            </Boton>
          </div>
        </Modal>
      )}

      {modal === 'editar' && (
        <Modal title="Editar beneficiario y dedicatoria" onClose={() => setModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={lblSx}>Beneficiario</label>
              <input value={edit.beneficiario_nombre} onChange={e => setEdit(v => ({ ...v, beneficiario_nombre: e.target.value }))} style={inputSx} />
            </div>
            <div>
              <label style={lblSx}>Email del beneficiario</label>
              <input type="email" value={edit.beneficiario_email} onChange={e => setEdit(v => ({ ...v, beneficiario_email: e.target.value }))} style={inputSx} />
            </div>
            <div>
              <label style={lblSx}>Dedicatoria (sale en el PDF)</label>
              <textarea rows={3} value={edit.dedicatoria} onChange={e => setEdit(v => ({ ...v, dedicatoria: e.target.value }))} style={{ ...inputSx, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
          </div>
          {error && <div style={{ color: colors.err, fontSize: text.sec, marginTop: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <Boton variante="ghost" onClick={() => setModal(null)}>Cancelar</Boton>
            <Boton variante="primary" disabled={procesando} onClick={() => accion(() => giftCardsApi.actualizar(card.id, edit))}>
              {procesando ? 'Guardando…' : 'Guardar'}
            </Boton>
          </div>
        </Modal>
      )}
    </div>
  )
}
